import { ApplicationView, useController } from '@aha-app/mvc';
import classnames from 'classnames';
import React from 'react';
import WhiteboardController from '../../../controllers/WhiteboardController';
import { zoomAndPanTo } from '../../../utils/zoom';
import type VotingSessionWidget from '../../../widgets/VotingSessionWidget';
import VotingSessionController from './controllers/VotingSessionController';
import { PokerResultsPanel } from './PokerResults';
import { PokerCard } from './PokerCard';
import { votingObjectIcon } from './Result';
import styles from './VotingSession.module.css';
import PokerVotingSessionResults from './PokerVotingSessionResults';
import locateIcon from '../../../assets/icons/pokerLocateRecord.svg';
import { svgToIcon } from '../../../utils/svg';

const LocateIcon = svgToIcon(locateIcon);

const AdminVotingTargetSelection = ({
  widget,
  selectedObjects,
  setSelectedObject,
}: {
  widget: VotingSessionWidget;
  selectedObjects: string[];
  setSelectedObject: (objectId: string | null) => void;
}) => {
  const previousVotingObject = widget.previousVotingObject;
  let previousObjectInfo: React.ReactNode;
  if (previousVotingObject) {
    if (!widget.pokerFinalScores) {
      throw new Error(`pokerFinalScores should have been created with widget`);
    }
    const previousObjectEstimate =
      widget.pokerFinalScores[previousVotingObject];
    previousObjectInfo = (
      <div className='flex-col gap-2 align-items-center'>
        <PokerCard
          cardSize='normal'
          cardColor='submitted'
          value={previousObjectEstimate}
        />
        <VotingObjectInfo
          recordId={previousVotingObject}
          showLocateButton={false}
        />
        <aha-pill color='var(--aha-green-10)'>Estimate saved</aha-pill>
      </div>
    );
  }

  const isFirstEstimation =
    !widget.pokerFinalScores ||
    Object.keys(widget.pokerFinalScores).length === 0;

  let selectedObject: string | null = null;
  if (selectedObjects.length === 0) {
    const alertText = isFirstEstimation
      ? 'Select an object to start the voting session.'
      : 'Select a single object to vote on next.';
    return (
      <>
        {previousObjectInfo}
        <div className={classnames('t-050', styles.pokerDetailsAlert)}>
          <aha-alert type='info' style={{ width: '100%' }}>
            {alertText}
          </aha-alert>
        </div>
      </>
    );
  } else if (selectedObjects.length === 1) {
    selectedObject = selectedObjects[0];
    if (!selectedObject) {
      throw new Error('this is not possible');
    }
    const buttonText = isFirstEstimation ? 'Begin estimation' : 'Next estimate';
    return (
      <>
        <VotingObjectInfo recordId={selectedObject} showLocateButton={false} />
        <aha-button
          kind='secondary'
          size='large'
          style={{ width: '100%' }}
          onClick={() => setSelectedObject(selectedObject)}
        >
          {buttonText}
        </aha-button>
      </>
    );
  }
  return (
    <div className='t-050 flex align-items-center gap-1'>
      <aha-alert type='warning' style={{ width: '100%' }}>
        Select only one object to vote on.
      </aha-alert>
    </div>
  );
};

const ActiveVotingPanel = ApplicationView(
  ({
    widget,
    isFacilitator,
  }: {
    widget: VotingSessionWidget;
    isFacilitator: boolean;
  }) => {
    const controller = useController(VotingSessionController);
    const currentVotingObject = controller.getCurrentVotingObject();

    let selectedCardIndex: number;
    if (currentVotingObject) {
      selectedCardIndex = controller.getVotesForObject(
        currentVotingObject.recordId
      );
    }

    return (
      <div className='mb-4 text-center'>
        <div className={styles.pokerCardListContainer}>
          <div className='mb-4 t-100'>Select your estimate</div>
          <div className={styles.pokerCardList}>
            {widget.pokerChoices?.map((choice, index) => {
              const isSelected = selectedCardIndex === index + 1;
              return (
                <PokerCard
                  cardSize='normal'
                  cardColor={isSelected ? 'selected' : 'normal'}
                  key={choice}
                  value={choice}
                  onClick={
                    currentVotingObject?.votingStatus === 'started'
                      ? () =>
                          controller.actionSetVotesForObject(
                            currentVotingObject.recordId,
                            isSelected ? 0 : index + 1
                          )
                      : undefined
                  }
                />
              );
            })}
          </div>
        </div>

        {isFacilitator &&
          currentVotingObject &&
          currentVotingObject.votingStatus === 'started' && (
            <div className='pt-5'>
              <aha-button
                kind='secondary'
                size='large'
                style={{ width: '100%' }}
                onClick={() =>
                  controller.actionCloseVotingOnObject(
                    currentVotingObject.recordId
                  )
                }
              >
                Stop voting and reveal
              </aha-button>
            </div>
          )}
      </div>
    );
  }
);

export const LocateButton = ApplicationView(
  ({ recordId }: { recordId: string }) => {
    const controller = useController(WhiteboardController);
    const votingSessionController = useController(VotingSessionController);
    const shape = votingSessionController.getShapeByRecordId(recordId);
    if (!shape) {
      return null;
    }
    return (
      <aha-button
        kind='plain'
        size='mini'
        onClick={() => {
          void zoomAndPanTo(controller, shape, { marginFactor: 0.6 });
        }}
      >
        <div className='flex align-items-center gap-1'>
          <span>Locate</span>
          {LocateIcon}
        </div>
      </aha-button>
    );
  }
);

const VotingObjectInfo = ApplicationView(
  ({
    recordId,
    showLocateButton,
  }: {
    recordId: string;
    showLocateButton: boolean;
  }) => {
    const votingSessionController = useController(VotingSessionController);
    const shape = votingSessionController.getShapeByRecordId(recordId);

    if (!shape) {
      return null;
    }

    return (
      <div className='flex-col align-items-center gap-2'>
        <div className='h-600 flex-col gap-2 align-items-center'>
          {votingObjectIcon(shape)}
          <span style={{ textAlign: 'center' }}>{shape.plainText()}</span>
        </div>
        {showLocateButton && <LocateButton recordId={recordId} />}
      </div>
    );
  }
);

/**
 * Shows the breakdown of votes for each card so that the facilitator
 * can select the final score.
 */
const PokerDetails = ({
  widget,
  isFacilitator,
}: {
  widget: VotingSessionWidget;
  isFacilitator: boolean;
}) => {
  const controller = useController(VotingSessionController);
  const currentVotingObject = controller.getCurrentVotingObject();
  const selectedObjects = controller.state.selectedObjects;

  let infoComponent: JSX.Element | null = null;
  let panel: JSX.Element | null = null;

  if (currentVotingObject) {
    infoComponent = (
      <VotingObjectInfo
        recordId={currentVotingObject.recordId}
        showLocateButton={currentVotingObject.votingStatus !== 'finished'}
      />
    );
    switch (currentVotingObject.votingStatus) {
      case 'finished': {
        panel = (
          <PokerResultsPanel
            widget={widget}
            recordId={currentVotingObject.recordId}
            userIsFacilitator={isFacilitator}
          />
        );
        break;
      }
      case 'started': {
        panel = (
          <ActiveVotingPanel widget={widget} isFacilitator={isFacilitator} />
        );
        break;
      }
      default: {
        const _exhaustivenessCheck: never = currentVotingObject.votingStatus;
        break;
      }
    }
  } else if (isFacilitator) {
    infoComponent = (
      <AdminVotingTargetSelection
        widget={widget}
        selectedObjects={selectedObjects}
        setSelectedObject={controller.actionSetSelectedObject}
      />
    );
  } else {
    infoComponent = (
      <div className='t-100'>
        Waiting for the facilitator to select an object for voting
      </div>
    );
  }

  return (
    <div className={styles.pokerDetails}>
      {infoComponent && (
        <div className={classnames('flex-col', 'align-items-center', 'gap-2')}>
          {infoComponent}
        </div>
      )}
      <div className={styles.pokerDetailsPanel}>{panel}</div>
      <PokerVotingSessionResults widget={widget} showHeader />
    </div>
  );
};

export default ApplicationView(PokerDetails);
